import {
  ArrowLeft,
  CheckCircle,
  KeyRound,
  Loader2,
  Lock,
  LogIn,
  Mail,
  Phone,
  Sparkles,
  User,
  UserPlus,
  X,
} from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

type AuthMode = 'login' | 'register' | 'guest' | 'forgot';

const AuthModal: React.FC = () => {
  const { showAuthModal, setShowAuthModal, login, register, guestCheckout } = useAuth();
  const [mode, setMode] = useState<AuthMode>('guest');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');

  if (!showAuthModal) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      if (mode === 'login') {
        await login(email, password);
      } else if (mode === 'register') {
        await register(name, email, password, phone || undefined);
      } else if (mode === 'forgot') {
        const response = await fetch('/api/auth/forgot-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        const data = await response.json();
        if (response.ok) {
          setSuccessMessage(data.message);
        } else {
          throw new Error(data.error || 'Failed to send reset email');
        }
      } else {
        await guestCheckout(name, email || undefined, phone || undefined);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName('');
    setEmail('');
    setPhone('');
    setPassword('');
    setError(null);
    setSuccessMessage(null);
  };

  const switchMode = (newMode: AuthMode) => {
    setMode(newMode);
    resetForm();
  };

  // Forgot password success screen
  if (mode === 'forgot' && successMessage) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={() => setShowAuthModal(false)}
        />
        <div
          className="relative w-full max-w-md glass rounded-3xl p-8 animate-fade-in text-center"
          style={{ borderColor: 'var(--border-color)' }}
        >
          <div className="w-16 h-16 bg-gradient-to-r from-green-400 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-on-gradient" />
          </div>
          <h2 className="text-2xl font-black mb-2" style={{ color: 'var(--text-primary)' }}>
            Check Your Email
          </h2>
          <p className="mb-6" style={{ color: 'var(--text-muted)' }}>
            {successMessage}
          </p>
          <button onClick={() => switchMode('login')} className="btn-neon">
            <ArrowLeft className="w-4 h-4 inline mr-2" />
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={() => setShowAuthModal(false)}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-md glass rounded-3xl p-8 animate-fade-in"
        style={{ borderColor: 'var(--border-color)' }}
      >
        {/* Close button */}
        <button
          onClick={() => setShowAuthModal(false)}
          className="absolute top-4 right-4 nav-link-inactive transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            {mode === 'login' ? (
              <LogIn className="w-8 h-8 text-on-gradient" />
            ) : mode === 'register' ? (
              <UserPlus className="w-8 h-8 text-on-gradient" />
            ) : mode === 'forgot' ? (
              <KeyRound className="w-8 h-8 text-on-gradient" />
            ) : (
              <User className="w-8 h-8 text-on-gradient" />
            )}
          </div>
          <h2 className="text-2xl font-black" style={{ color: 'var(--text-primary)' }}>
            {mode === 'login'
              ? 'Welcome Back!'
              : mode === 'register'
                ? 'Join Printy'
                : mode === 'forgot'
                  ? 'Reset Password'
                  : 'Quick Checkout'}
            <Sparkles className="inline w-5 h-5 text-accent-yellow ml-2 animate-pulse" />
          </h2>
          <p className="text-sm mt-2" style={{ color: 'var(--text-muted)' }}>
            {mode === 'login'
              ? 'Sign in to your account'
              : mode === 'register'
                ? 'Create an account for faster ordering'
                : mode === 'forgot'
                  ? "Enter your email and we'll send reset instructions"
                  : 'Just tell us who you are'}
          </p>
        </div>

        {/* Mode tabs */}
        {mode === 'forgot' ? (
          <button
            onClick={() => switchMode('login')}
            className="flex items-center gap-2 nav-link-inactive mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </button>
        ) : (
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => switchMode('guest')}
              className={`flex-1 py-2 px-3 rounded-xl text-sm font-bold transition-all ${
                mode === 'guest'
                  ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-on-gradient'
                  : 'surface-inactive hover:surface-inactive-hover'
              }`}
            >
              Guest
            </button>
            <button
              onClick={() => switchMode('login')}
              className={`flex-1 py-2 px-3 rounded-xl text-sm font-bold transition-all ${
                mode === 'login'
                  ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-on-gradient'
                  : 'surface-inactive hover:surface-inactive-hover'
              }`}
            >
              Login
            </button>
            <button
              onClick={() => switchMode('register')}
              className={`flex-1 py-2 px-3 rounded-xl text-sm font-bold transition-all ${
                mode === 'register'
                  ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-on-gradient'
                  : 'surface-inactive hover:surface-inactive-hover'
              }`}
            >
              Register
            </button>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 text-error rounded-xl text-sm">
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {/* Name (for guest and register) */}
          {(mode === 'guest' || mode === 'register') && (
            <div>
              <label
                className="block text-sm font-bold mb-2"
                style={{ color: 'var(--text-secondary)' }}
              >
                Your Name
              </label>
              <div className="relative">
                <User
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5"
                  style={{ color: 'var(--text-muted)' }}
                />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="What should we call you?"
                  className="w-full theme-input rounded-xl pl-10 pr-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none transition-all"
                  required
                />
              </div>
            </div>
          )}

          {/* Email */}
          <div>
            <label
              className="block text-sm font-bold mb-2"
              style={{ color: 'var(--text-secondary)' }}
            >
              Email{' '}
              {mode === 'guest' && (
                <span className="font-normal" style={{ color: 'var(--text-muted)' }}>
                  (or phone below)
                </span>
              )}
              {mode === 'forgot' && (
                <span className="font-normal" style={{ color: 'var(--text-muted)' }}>
                  (we'll send reset instructions)
                </span>
              )}
            </label>
            <div className="relative">
              <Mail
                className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5"
                style={{ color: 'var(--text-muted)' }}
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full theme-input rounded-xl pl-10 pr-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none transition-all"
                required={mode !== 'guest' || mode === 'forgot'}
              />
            </div>
          </div>

          {/* Phone (for guest and register) */}
          {(mode === 'guest' || mode === 'register') && (
            <div>
              <label
                className="block text-sm font-bold mb-2"
                style={{ color: 'var(--text-secondary)' }}
              >
                Phone{' '}
                {mode === 'register' && (
                  <span className="font-normal" style={{ color: 'var(--text-muted)' }}>
                    (optional)
                  </span>
                )}
              </label>
              <div className="relative">
                <Phone
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5"
                  style={{ color: 'var(--text-muted)' }}
                />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  className="w-full theme-input rounded-xl pl-10 pr-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none transition-all"
                />
              </div>
            </div>
          )}

          {/* Password (for login and register) */}
          {(mode === 'login' || mode === 'register') && (
            <div>
              <label
                className="block text-sm font-bold mb-2"
                style={{ color: 'var(--text-secondary)' }}
              >
                Password
              </label>
              <div className="relative">
                <Lock
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5"
                  style={{ color: 'var(--text-muted)' }}
                />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === 'register' ? 'At least 6 characters' : 'Your password'}
                  className="w-full theme-input rounded-xl pl-10 pr-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none transition-all"
                  required
                  minLength={mode === 'register' ? 6 : undefined}
                />
              </div>
            </div>
          )}

          {/* Forgot password link (for login mode) */}
          {mode === 'login' && (
            <div className="text-right">
              <button
                type="button"
                onClick={() => switchMode('forgot')}
                className="text-sm text-accent-blue hover:opacity-80 transition-colors"
              >
                Forgot password?
              </button>
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full btn-neon text-lg disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> : null}
            {mode === 'login'
              ? 'Sign In'
              : mode === 'register'
                ? 'Create Account'
                : mode === 'forgot'
                  ? 'Send Reset Link'
                  : 'Continue as Guest'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AuthModal;
